import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import Logo from "../../assets/images/logo.png";
import { authenticationService } from "../../api/authentication.service";
import { UserContext } from "../../contexts/UserContext";
import RedirectToPortal from "../MembershipPayment/RedirectToPortal";
import { useOnboarding } from "../../contexts/OnboardingContext";
import VideoModal from "./VideoModal";

function Header({ showNavigationToggle = false, isNavigationOpen = false, onNavigationToggle = () => {}, onAuthenticate }) {
	const user = useContext(UserContext);
	const { startTour, isTourAvailable } = useOnboarding();
	const [menuOpen, setMenuOpen] = useState(false);
	const [isVideoOpen, setIsVideoOpen] = useState(false);
	const [showPortalLoader, setShowPortalLoader] = useState(false);
	const dropdownRef = useRef(null);
	const accountButtonRef = useRef(null);
	const hasSession = !!user;
	const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email || "Account";
	const initials = [user?.first_name, user?.last_name]
		.filter(Boolean)
		.map((name) => name.charAt(0))
		.join("")
		.slice(0, 2)
		.toUpperCase() || "A";
	const closeVideo = useCallback(() => setIsVideoOpen(false), []);

	function handleLogout() {
		setMenuOpen(false);
		authenticationService.logout().catch((error) => console.error(error));
	}

	useEffect(() => {
		function closeMenu(event) {
			if (event.key === "Escape") {
				setMenuOpen(false);
				accountButtonRef.current?.focus();
				return;
			}

			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setMenuOpen(false);
			}
		}

		if (menuOpen) {
			document.addEventListener("mousedown", closeMenu);
			document.addEventListener("keydown", closeMenu);
		}

		return () => {
			document.removeEventListener("mousedown", closeMenu);
			document.removeEventListener("keydown", closeMenu);
		};
	}, [menuOpen]);

	return (
		<>
			{showPortalLoader && <RedirectToPortal />}
			{isVideoOpen ? <VideoModal onClose={closeVideo} /> : null}
			<header className="app-header">
				<div className="app-header__brand">
					{showNavigationToggle ? (
						<button
							type="button"
							className="app-header__nav-toggle"
							onClick={onNavigationToggle}
							aria-label={isNavigationOpen ? "Close navigation" : "Open navigation"}
							aria-expanded={isNavigationOpen}
							aria-controls="primary-navigation"
						>
							<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
								{isNavigationOpen ? (
									<path d="M5 5l14 14M19 5L5 19" />
								) : (
									<path d="M4 6h16M4 12h16M4 18h16" />
								)}
							</svg>
						</button>
					) : null}
					<Link to="/" className="app-header__logo-link" aria-label="ChampionBred home">
						<img className="logo" src={Logo} alt="ChampionBred" />
					</Link>
				</div>

				<div className="app-header__actions">
					<button type="button" className="app-header__icon-button app-header__video-button" onClick={() => { setMenuOpen(false); setIsVideoOpen(true); }} aria-label="Watch ChampionBred video" title="Watch ChampionBred video">
						<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
							<circle cx="12" cy="12" r="9" />
							<path d="m10 8 6 4-6 4z" fill="currentColor" stroke="none" />
						</svg>
					</button>
					{hasSession && isTourAvailable ? (
						<button
							type="button"
							className="app-header__icon-button onboarding-replay-button"
							onClick={startTour}
							aria-label="Restart onboarding tour"
							title="Restart onboarding tour"
						>
							<i className="fa fa-map-signs" aria-hidden="true" />
						</button>
					) : null}
					{hasSession ? (
						<>
							<Link to="/membership" className="app-header__credits" aria-label={`${user.credits ?? 0} credits. Manage credits`}>
								<i className="fa fa-coins" aria-hidden="true" />
								<span className="app-header__credits-value">{user.credits ?? 0}</span>
								<span className="app-header__credits-label">credits</span>
							</Link>
							<div className="app-header__account" ref={dropdownRef}>
								<button
									ref={accountButtonRef}
									type="button"
									className="app-header__account-button"
									onClick={() => setMenuOpen((open) => !open)}
									aria-expanded={menuOpen}
									aria-haspopup="menu"
								>
									<span className="app-header__avatar" aria-hidden="true">{initials}</span>
									<span className="app-header__account-name">{displayName}</span>
									<i className={`fa fa-chevron-${menuOpen ? "up" : "down"}`} aria-hidden="true" />
								</button>
								{menuOpen ? (
									<div className="menu-header" role="menu" aria-label="Account menu">
										<div className="menu-header__identity">
											<strong>{displayName}</strong>
											{user.email ? <span>{user.email}</span> : null}
										</div>
										<Link to="/my-credits" role="menuitem" onClick={() => setMenuOpen(false)}>
											<i className="fa fa-coins" aria-hidden="true" /> My credits
										</Link>
										<Link to="/transactions" role="menuitem" onClick={() => setMenuOpen(false)}>
											<i className="fa fa-receipt" aria-hidden="true" /> Transaction history
										</Link>
										<button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setShowPortalLoader(true); }}>
											<i className="fa fa-credit-card" aria-hidden="true" /> Manage payment
										</button>
										<button type="button" role="menuitem" className="menu-header__logout" onClick={handleLogout}>
											<i className="fa fa-sign-out" aria-hidden="true" /> Log out
										</button>
									</div>
								) : null}
							</div>
						</>
					) : (
						<div className="app-header__guest-actions">
							{onAuthenticate ? <>
								<button type="button" className="report-header-login" onClick={() => onAuthenticate("login")}>Log in</button>
								<button type="button" className="button outline" onClick={() => onAuthenticate("signup")}>Sign up</button>
							</> : <>
							<Link to="/login">Log in</Link>
							<Link to="/signup" className="button outline">Sign up</Link>
							</>}
						</div>
					)}
				</div>
			</header>
		</>
	);
}

export default Header;
